import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Play, Trash2, Info, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSeeding() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const isAdmin = user.is_admin || user.is_sysadmin || user.is_operator_admin || user.is_platform_admin;
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const runAction = async (label, method, url) => {
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(url, { method, credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(`${label} failed: ${data.detail || res.status}`);
        setResult({ label, ok: false, status: res.status, data });
      } else {
        toast.success(`${label} complete`);
        setResult({ label, ok: true, status: res.status, data });
      }
    } catch (err) {
      toast.error(`${label} error: ${err.message}`);
      setResult({ label, ok: false, error: err.message });
    } finally {
      setBusy(false);
    }
  };

  const seedCommunities = () =>
    runAction('Seed Communities examples', 'POST', `${API_URL}/api/subgroups/admin/seed-examples`);

  const auditExamples = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/subgroups/admin/examples`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(`Audit failed: ${data.detail || res.status}`);
      } else {
        setAudit(data);
      }
    } catch (err) {
      toast.error(`Audit error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const wipeExamples = async () => {
    if (!window.confirm('Delete every example community, membership, and post tagged is_example=true? This is destructive.')) return;
    await runAction('Wipe Communities examples', 'DELETE', `${API_URL}/api/subgroups/admin/examples`);
    setAudit(null);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="admin-seeding-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-amber-600" /> Admin Seeding
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          One-click seed and cleanup actions for operator tasks. All actions are idempotent.
        </p>
      </div>

      <div className="space-y-6">
        <Card data-testid="seed-communities-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-emerald-600" /> Communities (sub-clubs) examples
            </CardTitle>
            <CardDescription>
              Seeds 3 example communities (Golden Retriever Lovers, Training &amp; Tricks, NYC Dog Owners) with starter posts.
              Every record is tagged <code>is_example: true</code> so it can be audited or wiped later. Safe to re-run.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={seedCommunities} disabled={busy} data-testid="seed-communities-btn">
                {busy ? 'Working…' : 'Seed Communities examples'}
              </Button>
              <Button variant="outline" onClick={auditExamples} disabled={busy} data-testid="audit-communities-btn">
                Audit example data
              </Button>
              <Button variant="destructive" onClick={wipeExamples} disabled={busy} data-testid="wipe-communities-btn">
                <Trash2 className="h-4 w-4 mr-1" /> Wipe all examples
              </Button>
            </div>

            {audit && (
              <div className="mt-3 rounded-md border bg-slate-50 p-3 text-sm" data-testid="audit-results">
                <div className="font-semibold mb-1">Audit:</div>
                <ul className="ml-4 list-disc">
                  <li>Subgroups tagged is_example: <strong>{audit.subgroup_count}</strong></li>
                  <li>Memberships tagged is_example: <strong>{audit.example_member_records}</strong></li>
                  <li>Posts tagged is_example: <strong>{audit.example_posts}</strong></li>
                </ul>
                {audit.subgroups?.length > 0 && (
                  <ul className="ml-4 mt-2 list-disc text-slate-700">
                    {audit.subgroups.map((sg) => (
                      <li key={sg.id}>{sg.name} <span className="text-slate-400">/{sg.slug}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {result && (
              <div
                className={`mt-3 rounded-md border p-3 text-sm ${result.ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}
                data-testid="seed-result"
              >
                <div className="font-semibold">{result.label} — {result.ok ? 'OK' : `Failed (${result.status || 'error'})`}</div>
                <pre className="mt-1 overflow-x-auto text-xs">{JSON.stringify(result.data || result.error, null, 2)}</pre>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-sky-600" /> Other seed endpoints
            </CardTitle>
            <CardDescription>
              Reference for additional seed endpoints that accept a <code>bootstrap_key</code> query param. Run from any HTTP client.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="ml-4 list-disc text-sm space-y-1">
              <li><code>POST /api/admin/seed/breeds?bootstrap_key=...&amp;force=true</code> — 297 breeds</li>
              <li><code>POST /api/admin/seed/youtube-and-events?bootstrap_key=...</code> — curated channels + events</li>
              <li><code>POST /api/admin/seed/research?bootstrap_key=...</code> — research library</li>
              <li><code>POST /api/sponsors/admin/cleanup-demo?bootstrap_key=...</code> — purge demo sponsors</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
